import type { ConnectorDefinition } from "../types";

/**
 * monday.com Platform MCP tools that only READ. Always available once the
 * connector is live; never gated by the write flag. These map to monday
 * GraphQL queries (boards/items/docs/users/etc.) plus the schema-introspection
 * and planning helpers, which return data without mutating anything.
 */
export const MONDAY_READ_TOOLS = [
  "get_board_info",
  "get_board_items_page",
  "get_board_activity",
  "board_insights",
  "get_column_type_info",
  "list_workspaces",
  "workspace_info",
  "read_docs",
  "all_widgets_schema",
  "get_form",
  "get_user_context",
  "list_users_and_teams",
  "get_updates",
  "search",
  "get_assets",
  "list_automations",
  "plan_workflow",
  "agent_catalog",
  "get_notetaker_meetings",
  "get_monday_dev_sprints_boards",
  "get_sprints_metadata",
  "get_sprint_summary",
  "get_graphql_schema",
  "get_type_details",
] as const;

/**
 * monday.com Platform MCP tools that WRITE / mutate. Gated behind
 * `connectors.monday.write`: with the write flag off they never materialize for
 * the model (filtered in `defineMcpCapability.resolveTools`), so the connector
 * ships read-only first. Imported by the monday capability so the connector
 * definition stays the single source of truth for the split.
 *
 * The same list drives HITL: the capability declares an approval for every tool
 * here (ADR-050), so a write the user hasn't trusted prompts before it runs.
 * Adding a name here without a label there is a compile error.
 *
 * Nothing here deletes a board, item, column, group, or doc — monday's raw
 * GraphQL tool, the only route to those mutations, is excluded from the
 * connector outright (see {@link MONDAY_EXCLUDED_TOOLS}). Several tools do
 * remove configuration: `manage_automations` can delete an automation,
 * `manage_agent` an agent, and `form_questions_editor` a form question.
 */
export const MONDAY_WRITE_TOOLS = [
  "create_board",
  "create_group",
  "create_column",
  "create_item",
  "change_item_column_values",
  "create_workspace",
  "update_workspace",
  "create_folder",
  "update_folder",
  "move_object",
  "create_doc",
  "update_doc",
  "create_dashboard",
  "create_widget",
  "create_view",
  "update_view",
  "create_view_table",
  "update_view_table",
  "create_form",
  "update_form",
  "form_questions_editor",
  "create_form_submission",
  "create_update",
  "create_notification",
  "get_asset_upload_url",
  "finalize_asset_upload",
  "manage_automations",
  "create_automation",
  "create_workflow",
  "update_workflow",
  "publish_workflow",
  "manage_agent",
  "manage_agent_triggers",
  "manage_agent_skills",
  "manage_agent_knowledge",
] as const;

/**
 * monday.com MCP tools deliberately NOT wired up. Absent from `toolAllowlist`,
 * so `discoverMcpTools` drops them from the catalog and they never reach the
 * capability, the model's tool set, or the progressive-disclosure manifest —
 * no feature flag or approval can turn them on.
 *
 * `all_monday_api` executes any GraphQL query or mutation against monday's API,
 * which makes it the connector's entire delete surface for real work:
 * `delete_item`, `delete_board`, `delete_column`, `delete_group`,
 * `delete_update`, and the `archive_*` family have no dedicated tool and are
 * reachable only through it. It is also the one tool an approval card cannot
 * describe — every call renders the same way whatever the query does — so the
 * user approving it has to read GraphQL to know what they're allowing. The 35
 * specific write tools cover the same ground for anything Magnus should be
 * doing unattended.
 *
 * Listed (not simply deleted) so the exclusion survives someone regenerating
 * this file from monday's tool docs; a test asserts it stays off the allowlist.
 */
export const MONDAY_EXCLUDED_TOOLS = ["all_monday_api"] as const;

/**
 * monday.com connector — OAuth-backed MCP. Authentication uses monday's
 * standard OAuth 2.0 authorization-code flow (monday is the identity provider
 * at `auth.monday.com`); the resulting per-user access token is injected as
 * `Authorization: Bearer` into the hosted Platform MCP server. monday OAuth
 * tokens are permanent bearer tokens and have no refresh flow, so the missing
 * `expires_in` simply leaves the connector with no expiry.
 *
 * `force_install_if_needed=true` lets the consent flow install the monday MCP
 * app on the account if it isn't already, so connecting works even before an
 * admin has installed it. PKCE is omitted because monday does not support it.
 */
export const mondayConnector: ConnectorDefinition = {
  id: "monday",
  name: "monday.com",
  description: "Read and update your monday.com boards, items, docs, and more",
  icon: "monday",
  authType: "oauth",
  kind: "mcp",
  oauth: {
    authorizationUrl: "https://auth.monday.com/oauth2/authorize",
    tokenUrl: "https://auth.monday.com/oauth2/token",
    // Source of truth for granted scopes is the monday Developer Center app
    // config, NOT this list (see `omitScopeParam` below). This array documents
    // the scopes the live app version must have enabled, and keeps the
    // connector from being treated as user-token-only.
    scopes: [
      // Reads
      "me:read",
      "account:read",
      "boards:read",
      "docs:read",
      "updates:read",
      "assets:read",
      "tags:read",
      "teams:read",
      // Writes — gated in Magnus by `connectors.monday.write`; configured on the
      // app once so enabling writes later needs no re-consent.
      "boards:write",
      "docs:write",
      "updates:write",
      "notifications:write",
    ],
    // Don't send `scope` in the authorize request. monday auto-approves every
    // scope for app collaborators but strictly validates the requested set
    // against the live app config for everyone else, so a hard-coded list that
    // doesn't exactly match the configured scopes fails non-collaborators with
    // `invalid_scope` (worked for the developer, broke for all other users).
    // Omitting it makes monday grant exactly the app's configured scopes.
    omitScopeParam: true,
    clientIdEnvVar: "MONDAY_CLIENT_ID",
    clientSecretEnvVar: "MONDAY_CLIENT_SECRET",
    authParams: { force_install_if_needed: "true" },
  },
  mcp: {
    serverUrl: "https://mcp.monday.com/mcp",
    transport: "http",
    toolAllowlist: [...MONDAY_READ_TOOLS, ...MONDAY_WRITE_TOOLS],
    writeToolNames: [...MONDAY_WRITE_TOOLS],
  },
  isEnabled: () =>
    Boolean(process.env.MONDAY_CLIENT_ID && process.env.MONDAY_CLIENT_SECRET),
};
